import dayjs from 'dayjs';

/**
 * Calculate monthly mortgage payment using standard amortization formula.
 * M = P * [r(1+r)^n] / [(1+r)^n - 1]
 * For interest-only: M = P * r
 */
export const calculateMonthlyPayment = (principal, annualRate, termMonths, repaymentType = 'repayment') => {
  if (principal <= 0 || termMonths <= 0) return 0;
  if (annualRate <= 0) return repaymentType === 'repayment' ? principal / termMonths : 0;

  const monthlyRate = annualRate / 100 / 12;

  if (repaymentType === 'interest-only') {
    return principal * monthlyRate;
  }

  const factor = Math.pow(1 + monthlyRate, termMonths);
  return principal * (monthlyRate * factor) / (factor - 1);
};

/**
 * Generate full amortization schedule month-by-month.
 * Handles UK fixed-then-SVR rate structure.
 * Returns array of entries: { month, date, payment, principal, interest, balance, rate, cumInterest, cumPrincipal }
 */
export const generateAmortizationSchedule = (mortgage) => {
  const {
    currentBalance,
    startDate,
    termYears,
    fixedRate,
    fixedTermEndDate,
    svrRate,
    overpaymentMonthly = 0,
    repaymentType = 'repayment',
  } = mortgage;

  const totalMonths = termYears * 12;
  const start = dayjs(startDate);
  const fixedEnd = dayjs(fixedTermEndDate);
  const schedule = [];

  let balance = currentBalance;
  let cumInterest = 0;
  let cumPrincipal = 0;

  // Calculate how many months have already elapsed
  const now = dayjs();
  const monthsElapsed = Math.max(0, now.diff(start, 'month'));
  const remainingMonths = Math.max(0, totalMonths - monthsElapsed);

  // Start schedule from now
  let currentRate = fixedRate;
  let currentPayment = calculateMonthlyPayment(balance, currentRate, remainingMonths, repaymentType);

  for (let month = 1; month <= remainingMonths && balance > 0.01; month++) {
    const monthDate = now.add(month, 'month');

    // Check if we've crossed into SVR territory
    const prevRate = currentRate;
    if (monthDate.isAfter(fixedEnd)) {
      currentRate = svrRate;
    } else {
      currentRate = fixedRate;
    }

    // Recalculate payment if rate changed
    if (currentRate !== prevRate) {
      const monthsLeft = remainingMonths - month + 1;
      currentPayment = calculateMonthlyPayment(balance, currentRate, monthsLeft, repaymentType);
    }

    const monthlyRate = currentRate / 100 / 12;
    const interestPortion = balance * monthlyRate;
    let principalPortion;

    if (repaymentType === 'interest-only') {
      principalPortion = overpaymentMonthly;
    } else {
      principalPortion = currentPayment - interestPortion + overpaymentMonthly;
    }

    // Don't overpay past zero
    principalPortion = Math.min(principalPortion, balance);
    const totalPayment = interestPortion + principalPortion;

    balance -= principalPortion;
    if (balance < 0.01) balance = 0;

    cumInterest += interestPortion;
    cumPrincipal += principalPortion;

    schedule.push({
      month,
      date: monthDate.format('YYYY-MM-DD'),
      displayDate: monthDate.format('MMM YYYY'),
      payment: totalPayment,
      principal: principalPortion,
      interest: interestPortion,
      balance,
      rate: currentRate,
      cumInterest,
      cumPrincipal,
    });
  }

  return schedule;
};

/**
 * Calculate total interest paid over life of mortgage.
 */
export const calculateTotalInterest = (mortgage) => {
  const schedule = generateAmortizationSchedule(mortgage);
  if (schedule.length === 0) return { totalInterest: 0, totalPaid: 0 };

  const last = schedule[schedule.length - 1];
  return {
    totalInterest: last.cumInterest,
    totalPaid: last.cumInterest + last.cumPrincipal,
  };
};

/**
 * Calculate impact of overpayments on term and interest saved.
 */
export const calculateOverpaymentImpact = (mortgage, monthlyOverpayment) => {
  const withoutOverpayment = generateAmortizationSchedule({ ...mortgage, overpaymentMonthly: 0 });
  const withOverpayment = generateAmortizationSchedule({ ...mortgage, overpaymentMonthly: monthlyOverpayment });

  const originalMonths = withoutOverpayment.length;
  const newMonths = withOverpayment.length;

  const originalInterest = withoutOverpayment.length > 0 ? withoutOverpayment[withoutOverpayment.length - 1].cumInterest : 0;
  const newInterest = withOverpayment.length > 0 ? withOverpayment[withOverpayment.length - 1].cumInterest : 0;

  return {
    monthsSaved: originalMonths - newMonths,
    interestSaved: originalInterest - newInterest,
    newTermMonths: newMonths,
    originalTermMonths: originalMonths,
  };
};

/**
 * Project property value into future using compound growth.
 */
export const projectPropertyValue = (currentValue, annualGrowthRate, months) => {
  if (months <= 0) return currentValue;
  const monthlyRate = annualGrowthRate / 100 / 12;
  return currentValue * Math.pow(1 + monthlyRate, months);
};

/**
 * Calculate equity = property value - mortgage balance.
 */
export const calculateEquity = (propertyValue, mortgageBalance) => {
  return propertyValue - (mortgageBalance || 0);
};

/**
 * Calculate LTV (Loan to Value) ratio as percentage.
 */
export const calculateLTV = (mortgageBalance, propertyValue) => {
  if (!propertyValue || propertyValue <= 0) return 0;
  return (mortgageBalance / propertyValue) * 100;
};

/**
 * Get current mortgage balance from the amortization schedule at a given month.
 */
export const getBalanceAtMonth = (mortgage, monthsFromNow) => {
  const schedule = generateAmortizationSchedule(mortgage);
  if (monthsFromNow <= 0) return mortgage.currentBalance;
  if (monthsFromNow >= schedule.length) return 0;
  return schedule[monthsFromNow - 1].balance;
};

/**
 * Calculate remaining term in months from now.
 */
export const calculateRemainingTerm = (mortgage) => {
  const start = dayjs(mortgage.startDate);
  const endDate = start.add(mortgage.termYears, 'year');
  const now = dayjs();
  return Math.max(0, endDate.diff(now, 'month'));
};

/**
 * Generate combined projection data for property value vs mortgage balance.
 * Used for the combined chart on property detail, mortgage detail, and real estate overview.
 */
export const generatePropertyMortgageProjection = (property, mortgage, projectionMonths = null) => {
  const months = projectionMonths || (mortgage ? calculateRemainingTerm(mortgage) : 300);
  const cappedMonths = Math.min(months, 600); // Cap at 50 years

  const schedule = mortgage ? generateAmortizationSchedule(mortgage) : [];
  const data = [];

  const now = dayjs();

  // Starting point
  data.push({
    month: 0,
    date: now.format('YYYY-MM-DD'),
    displayDate: now.format('MMM YYYY'),
    propertyValue: property ? property.currentValue : 0,
    mortgageBalance: mortgage ? mortgage.currentBalance : 0,
    equity: (property ? property.currentValue : 0) - (mortgage ? mortgage.currentBalance : 0),
  });

  for (let m = 1; m <= cappedMonths; m++) {
    const monthDate = now.add(m, 'month');
    const propValue = property ? projectPropertyValue(property.currentValue, property.annualGrowthRate, m) : 0;
    const mortBalance = m <= schedule.length ? schedule[m - 1].balance : 0;

    data.push({
      month: m,
      date: monthDate.format('YYYY-MM-DD'),
      displayDate: monthDate.format('MMM YYYY'),
      propertyValue: propValue,
      mortgageBalance: mortBalance,
      equity: propValue - mortBalance,
    });
  }

  return data;
};

/**
 * Calculate the monthly payment display info for a mortgage.
 */
export const getMortgagePaymentInfo = (mortgage) => {
  const remainingMonths = calculateRemainingTerm(mortgage);
  const now = dayjs();
  const fixedEnd = dayjs(mortgage.fixedTermEndDate);
  const isInFixedPeriod = now.isBefore(fixedEnd);
  const currentRate = isInFixedPeriod ? mortgage.fixedRate : mortgage.svrRate;
  const monthlyPayment = calculateMonthlyPayment(
    mortgage.currentBalance,
    currentRate,
    remainingMonths,
    mortgage.repaymentType
  );

  return {
    monthlyPayment,
    currentRate,
    isInFixedPeriod,
    remainingMonths,
    fixedMonthsRemaining: isInFixedPeriod ? fixedEnd.diff(now, 'month') : 0,
  };
};
